import { ChevronRightRounded, Delete, Preview } from '@mui/icons-material'
import { Box, Button, Chip, CircularProgress, FormControlLabel, Modal, Stack, Switch, Tooltip, Typography } from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import ApiClient from '../../../backend/ApiClient'
import algo from '../../../core/Algo'
import { IContractSchema } from '../../../definitions/ContractSchema'
import { IFormField } from '../../../definitions/FormField'
import Helper from '../../../helpers/Helper'
import { useAppSelector } from '../../../store/Hooks'
import useQuery, { buildQueryKey } from '../../hooks/useQuery'
import HelixLoader from '../../loaders/HelixLoader'
import RoutingManager from '../../routing/Routing'
import JsonPreviewView from './JsonPreviewView'
import NewSchemaEditor from './NewSchemaEditor'
import VerticalSplitView from './VerticalSplitView'
import { EditorField, EditorStep, useSchemaSteps } from './schemaEditorReducer'
import SchemaSubmitModal from '../schema_builder/SchemaManageModal'
import { snackbar } from '../../services/SnackbarService'
import useInvalidate from '../../hooks/useInvalidate'
import useCommand from '../../hooks/useCommand'
import { changeRoute } from '../../../store/slices/SessionSlice'
import { useDispatch } from 'react-redux'
import { ContractSchemaManager } from '../../../managers/ContractsSchemaManager'
import API_URLS from '../../../backend/ApiUrls'
import { modals } from '../../services/ModalService'
import PermissionsWrapper from '../../permissions/PermissionsWrapper'
import SchemaPreview from '../schema_builder/SchemaPreview'
import { statusOptions } from '../../../definitions/Approval'

export const isDuplicateKey = (fields: EditorField[], key: string, currentId: string) => {
    return fields.some(
        f => f.key === key && f._id !== currentId
    )
}

const mapFieldToEditorField = (field: IFormField): EditorField => {
    return {
        _id: Helper.generateUUID(),
        label: field.label,
        key: field.key,
        type: field.type,
        description: field.description,
        validation: field.validation,
        systemConfigType: field.systemConfigType,
        padu: field?.padu,
        stringOptionsType: field.stringOptionsType,
        isDefault: field.isDefault,
        childFields: field.childFields?.map(mapFieldToEditorField),
        objectFields: field.objectFields?.map(mapFieldToEditorField)
    }
}
const mapEditorFieldToSchemaField = (field: EditorField): IFormField => {
    return {
        label: field.label,
        key: field.key,
        type: field.type,
        description: field.description,
        validation: field.validation,
        systemConfigType: field.systemConfigType,
        stringOptionsType: field.stringOptionsType,
        isDefault: field.isDefault,
        padu: field?.padu,
        childFields: field.childFields?.map(mapEditorFieldToSchemaField),
        objectFields: field.objectFields?.map(mapEditorFieldToSchemaField)
    }
}

const renderLoading = () => {
    return (
        <Stack sx={{ height: '100%', width: '100%', justifyContent: 'center', alignItems: 'center' }}>
            <HelixLoader />
        </Stack>
    )
}

const renderError = () => (
    <Stack sx={{ height: '100%', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Typography variant='h6' color='error'>
            Something went wrong. Please try again later.
        </Typography>
    </Stack>
)

const SchemaManager = () => {
    const [state, dispatch] = useSchemaSteps()
    const [showJson, setShowJson] = useState<boolean>(false)
    const params = RoutingManager.getParams<{ uuid: string }>(useAppSelector(s => s.session.routeParams))
    const { status: schemaStatus, response: schemaD } = useQuery({
        queryKey: buildQueryKey('get-schema', params.uuid),
        queryFn: () => ApiClient.getContractSchema(params.uuid),
        cache: true
    })

    const { status: approvalStatus, response: approvalData } = useQuery({
        queryKey: buildQueryKey('entity-approval', params.uuid),
        queryFn: async () => await ApiClient.getEntityApproval(params.uuid),
        cache: true
    })

    const crDispatch = useDispatch()
    const invalidateList = useInvalidate({ queryKey: buildQueryKey(API_URLS.os.getAllContractSchemas + '/*') })
    const invalidateSchemaApprovalStatus = useInvalidate({ queryKey: buildQueryKey('entity-approval/*') })
    const invalidateSchemaVersions = useInvalidate({ queryKey: buildQueryKey('schema-versions/*') })
    const [submitModalOpen, setSubmitModalOpen] = useState(false)
    const [submitModalPreviewOpen, setSubmitModalPreviewOpen] = useState(false)
    const { execute: executeAddVersion, status: statusAddVersion } = useCommand({
        commandFn: (schemaUUID: string) => ApiClient.addContractSchemaVersion(schemaUUID),
        onFinish: result => {
            if (result.response) {
                snackbar.show({ severity: 'success', text: 'Schema new version cloned successfully' })
                invalidateSchemaApprovalStatus()
                invalidateSchemaVersions()
                crDispatch(changeRoute({
                    route: 'contract-schema-editor',
                    params: { uuid: result.response.uuid }
                }))
            }
        }
    })
    const { execute: executeSaveSchema, status: statusSaveSchema } = useCommand({
        commandFn: async (updatedSchema: IContractSchema) => {
            return ApiClient.updateContractSchema(updatedSchema)
        },
        onFinish: result => {
            if (result.response) {
                invalidateList()
                snackbar.show({ severity: 'success', text: 'Schema saved successfully!' })
            } else if (result.errorText) {
                snackbar.show({ severity: 'error', text: 'Error saving schema' })
            }
        }
    })
    const handleSave = useCallback(async (): Promise<IContractSchema> => {
        if (!schemaD) {
            throw new Error('Schema data not available')
        }

        const updatedSchema: IContractSchema = {
            ...schemaD,
            name: state.label,
            steps: state.steps.map(step => ({
                label: step.label,
                isDefault: step.isDefault,
                fields: step.fields.map(mapEditorFieldToSchemaField)
            })),

            lastUpdate: new Date().toISOString()
        }

        const validationErrors = ContractSchemaManager.validateSchema(updatedSchema.steps)
        if (validationErrors.length > 0) {
            snackbar.show({ severity: 'error', text: `${validationErrors}` })
            throw new Error(validationErrors.join(', '))
        }

        await executeSaveSchema(updatedSchema)
        return updatedSchema
    }, [schemaD, state.label, state.steps, executeSaveSchema])
    const handleDelete = useCallback(() => {
        modals.openConfirm({
            title: 'Confirm Schema Removal',
            children: <p>Are you sure you want to remove the Schema?</p>,
            labels: { confirm: 'Yes, Remove', cancel: 'Cancel' },
            colors: { confirm: 'error', cancel: 'primary' },
            onConfirm: async () => {
                try {
                    await ApiClient.deleteContractSchema(params.uuid)
                    invalidateList()
                    invalidateSchemaVersions()
                    crDispatch(changeRoute({ route: 'contracts', params: { tab: 'schemas' } }))
                    snackbar.show({ severity: 'success', text: 'Schema deleted successfully!' })
                } catch (error) {
                    console.error(error)
                    snackbar.show({ severity: 'error', text: error.message || 'Error deleting schema' })
                }
            }
        })
    }, [params.uuid, dispatch, invalidateList])

    const isReadOnly = useMemo(() => {
        if (approvalStatus === 'loading') return true
        if (approvalData?.status === 'approved' || approvalData?.status === 'ready-for-review') return true
        if (schemaD?.versioning?.isHead === true) return true

        return false
    }, [approvalData, schemaD?.versioning?.isHead, approvalStatus, schemaD])

    const hasError = schemaStatus === 'error'
    const finishedLoading = schemaStatus === 'success' && algo.hasVal(schemaD)

    useEffect(() => {
        if (finishedLoading) {
            const mappedSteps: EditorStep[] = schemaD.steps.map(step => ({
                _id: Helper.generateUUID(),
                isDefault: step.isDefault,
                label: step.label,
                fields: step.fields.map(mapFieldToEditorField)
            }))

            dispatch({ type: 'LOAD_SCHEMA', steps: mappedSteps, label: schemaD.name })
        }
    }, [schemaD])

    const derivedSchema: IContractSchema = {
        ...schemaD,
        steps: state.steps.map(step => ({
            label: step.label,
            isDefault: true,
            fields: step.fields.map(mapEditorFieldToSchemaField)
        }))
    }

    const displayStatus = useMemo(() => {
        if (approvalData?.status) return approvalData.status
        if (schemaD?.versioning?.isHead) return 'legacy' // for legacy schema whit no approval

        return 'draft'
    }, [approvalData?.status, schemaD?.versioning?.isHead])

    if (schemaStatus === 'loading') return

    return (
        <>
            {hasError
                ? renderError()
                : finishedLoading
                    ? (
                            <Stack sx={{ height: '100%', overflow: 'hidden' }}>
                                <Stack direction='row' justifyContent='space-between' sx={{ p: 1 }}>
                                    <Stack direction='row'>
                                        <Tooltip arrow title='Schema Title'>
                                            <Typography variant='h5' fontWeight='bold'>
                                                {state.label}
                                            </Typography>
                                        </Tooltip>
                                        {(approvalData || schemaD.versioning?.isHead !== undefined) && (
                                            <Tooltip arrow title='Status'>
                                                <Chip
                                                    sx={{
                                                        borderRadius: '999px',
                                                        fontWeight: 600,
                                                        ml: 2
                                                    }}
                                                    label={(
                                                        <Box display='flex' alignItems='center' gap={1}>
                                                            <Box
                                                                width={8}
                                                                height={8}
                                                                borderRadius='50%'
                                                                bgcolor={
                                                                    displayStatus === 'approved'
                                                                        ? 'success.main'
                                                                        : displayStatus === 'ready-for-review'
                                                                            ? 'info.main'
                                                                            : displayStatus === 'rejected'
                                                                                ? 'error.main'
                                                                                : 'warning.main'
                                                                }
                                                            />
                                                            {approvalStatus === 'loading'
                                                                ? <CircularProgress disableShrink size={17} />
                                                                : (
                                                                        <Typography variant='body2'>
                                                                            {statusOptions.find(option => option.value === displayStatus)?.label || 'no approval'}
                                                                        </Typography>
                                                                    )}

                                                        </Box>
                                                    )}
                                                    variant='outlined'
                                                    size='medium'
                                                />
                                            </Tooltip>
                                        )}
                                    </Stack>

                                    <Stack spacing={2} direction='row'>
                                        <Tooltip arrow title='Toggle between JSON View and Simple Step View'>
                                            <FormControlLabel
                                                labelPlacement='start'
                                                label={showJson ? 'Simple View' : 'Advanced View'}
                                                control={(
                                                    <Switch
                                                        checked={showJson}
                                                        onChange={() => setShowJson(prev => !prev)}
                                                    />
                                                )}
                                            />
                                        </Tooltip>
                                        <PermissionsWrapper permission='contract-schema.delete'>
                                            <Tooltip arrow title={schemaD.isDefault ? 'Cannot Delete a Default Schema' : 'Delete Schema'}>
                                                <span>
                                                    <Button
                                                        disabled={schemaD.isDefault || isReadOnly}
                                                        variant='outlined'
                                                        color='error'
                                                        startIcon={<Delete />}
                                                        onClick={handleDelete}>
                                                        Delete
                                                    </Button>
                                                </span>
                                            </Tooltip>
                                        </PermissionsWrapper>
                                        <Tooltip arrow title='Preview This Schema On The Contract Editor'>
                                            <Button
                                                onClick={() => setSubmitModalPreviewOpen(true)}
                                                startIcon={<Preview />}
                                                variant='outlined'>
                                                Preview
                                            </Button>
                                        </Tooltip>
                                        <PermissionsWrapper permission='contract-schema.edit'>
                                            <Tooltip arrow title={schemaD.isDefault ? 'Cannot Edit a Default Schema' : 'Manage Schema'}>
                                                <span>
                                                    <Button
                                                        variant='contained'
                                                        color='primary'
                                                        onClick={() => setSubmitModalOpen(true)}
                                                        endIcon={<ChevronRightRounded />}>
                                                        Manage
                                                    </Button>
                                                </span>
                                            </Tooltip>
                                        </PermissionsWrapper>
                                    </Stack>
                                </Stack>

                                <Stack
                                    sx={{
                                        flex: 1,
                                        minHeight: 0,
                                        display: 'flex'
                                    }}>
                                    <VerticalSplitView
                                        left={<NewSchemaEditor state={state} dispatch={dispatch} isDefault={schemaD.isDefault} />}
                                        right={showJson ? <JsonPreviewView schema={derivedSchema} /> : null}
                                    />
                                </Stack>

                                <Box>
                                    <SchemaSubmitModal
                                        open={submitModalOpen}
                                        onAddVersion={x => executeAddVersion(x.uuid)}
                                        onClose={() => setSubmitModalOpen(false)}
                                        schema={{
                                            ...schemaD,
                                            steps: state.steps
                                        }}
                                        onSave={handleSave}
                                        approval={approvalData}
                                        saveStatus={statusSaveSchema}
                                        statusAddVersion={statusAddVersion}
                                    />
                                    <Modal open={submitModalPreviewOpen} onClose={() => setSubmitModalPreviewOpen(false)}>
                                        <SchemaPreview
                                            schemaData={schemaD.steps}
                                            onClose={() => setSubmitModalPreviewOpen(false)}
                                        />
                                    </Modal>
                                </Box>

                            </Stack>
                        )
                    : renderLoading()}
        </>
    )
}
export default SchemaManager
